import { ReactNode, ReactElement, lazy } from 'react';
import _ from 'lodash';
import { IconURIPath, WidgetLocalizationKeys, LazyWidget } from '@UI/Widgets/Things/WidgetsThingsStructs';
import { IWidgetThingsClass } from "../Utils/IWidgetThingsClass";
import { isStringEmpty, isDefined, getRandomSmallKey, IsNullOrEmpty } from "@Utils/Micro";
import { DebugEx } from "@Utils/DebugEx";
import { ToolbarIconInfo } from '@Primitive/ToolbarIconButton';
import { IWidgetDescription, WidgetDescriptionToPlegmaThing, IWidgetDataValidateResp } from '@UI/Widgets/WidgetStructs';
import { WebNode } from '@API/WebNode';
import { WebThingPortType } from '@API/WebNodeTypes';
import { WebThing, WebThingPort } from '@API/WebThing';
import { Plegma } from '@API/Api';
import { LocalizationMap } from '@Primitive/Localization/Localization';
import { ColumnType, IconType, ReactTableStatics } from '../../../Tables/ReactTable';

import {
    TableData,
    DefaultColumnSettings,
    ColumnSettings,
    TableKeys,
    TableSettings,
    LocalizationLabelKey,
    DefaultTableSettings,
    getDefaultPageListSettings,
    editRowTooltip,
    settingsValidator,
    serverDataValidator,
    customColumnsValidator,
    EnumTypesJsonSchemeValidator,
    tableDataJsonSchemaValidator
} from './TableStructs';

const Table = lazy(() => import('./TableAsync'));
const TableWidgetSettings = lazy(() => import('./TableSettingsAsync'));
// ----------------------------------------------------------------------------------------------------
export class TableWidget extends IWidgetThingsClass<TableData> {
    // ------------------------------------------------------------------------------------------------
    public Render(key: string, width: number, height: number): ReactNode {
        return (
            <LazyWidget>
                <Table />
            </LazyWidget>
        );
    }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <LazyWidget>
            <TableWidgetSettings
                key={"Settings-" + key}
                width={width}
                height={height}
                WidgetData={_.cloneDeep(this.WidgetData)} // copy of the data and is return the copy in the update cb
                WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
                onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
                ThingKey={this.ThingKey}
                NodeKey={this.NodeKey}
                SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
            />
        </LazyWidget>;
    }
    // ------------------------------------------------------------------------------------------------
    updateThing() {
        let webNode = WebNode.GetInstanceKey(this.NodeKey);
        let thing = webNode.GetThing(this.ThingKey);

        let savedSettings: TableSettings = this.WidgetData;
        if (savedSettings != null) {
            // Clean old ports
            thing.Ports = {};

            // Add the ports
            this._GetPorts(savedSettings).forEach((port) => {
                thing.AddPort(port, false);
            });
        }

        webNode.GetModule(this.WebModuleKey).UpdateThing(thing);
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        let wData = this.WidgetData;

        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Ports
        let ports = this._GetPorts(wData);

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        if (this.WidgetData.Key == key || TableKeys.InsertOrUpdatePortKey == key)
            return tableDataJsonSchemaValidator().DataValidateString(value);
        else if (key == TableKeys.ServerData)
            return serverDataValidator().DataValidateString(value);
        else if (key == TableKeys.Settings)
            return settingsValidator().DataValidateString(value);
        else if (key == TableKeys.CustomColumns)
            return customColumnsValidator().DataValidateString(value);
        else
            return EnumTypesJsonSchemeValidator().DataValidateString(value);
    }
    // ------------------------------------------------------------------------------------------------
    private _GetPorts(tableSettings: TableSettings): WebThingPort[] {
        let ports: WebThingPort[] = [];

        if (tableSettings != null) {
            ports.push(new WebThingPort(
                {
                    Name: "Data",
                    PortKey: tableSettings.Key,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Input,
                    State: ""
                },
                WebThingPortType.Data,
                (data) => this.ValidateValues(tableSettings.Key, data)
            ));

            ports.push(new WebThingPort(
                {
                    Name: 'Insert or Update',
                    PortKey: TableKeys.InsertOrUpdatePortKey,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Input,
                    State: ""
                },
                WebThingPortType.Data,
                (data) => this.ValidateValues(TableKeys.InsertOrUpdatePortKey, data)));

            ports.push(new WebThingPort(
                {
                    Name: 'Settings',
                    PortKey: TableKeys.Settings,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Input,
                    State: ''
                },
                WebThingPortType.Data,
                data => this.ValidateValues(TableKeys.Settings, data))
            );

            ports.push(new WebThingPort(
                {
                    Name: 'Custom Columns',
                    PortKey: TableKeys.CustomColumns,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Input,
                    State: ''
                },
                WebThingPortType.Data,
                data => this.ValidateValues(TableKeys.CustomColumns, data))
            );

            for (let i = 0; i < tableSettings.Columns.length; i++) {
                const column = tableSettings.Columns[i];
                // check if button column
                if (column.Type == ColumnType.button) {
                    ports.push(new WebThingPort(
                        {
                            Name: column.Title,
                            PortKey: column.Key,
                            Type: Plegma.ePortType.JsonString,
                            ioDirection: Plegma.ioPortDirection.Output,
                            State: ""
                        },
                        WebThingPortType.Trigger)
                    );
                }
                // check if enum column
                else if (column.Type == ColumnType.enum) {

                    // try find enum
                    const enumEntry = _.find(tableSettings.EnumSettings, (e) => e.key == column.Key);
                    if (!isDefined(enumEntry)) {
                        DebugEx.TraceCritical("Should not be here");
                        continue;
                    }

                    ports.push(new WebThingPort(
                        {
                            Name: column.Title,
                            PortKey: column.Key,
                            Type: Plegma.ePortType.JsonString,
                            ioDirection: Plegma.ioPortDirection.Input,
                            State: ""
                        },
                        WebThingPortType.Data,
                        (data) => this.ValidateValues(column.Key, data))
                    );
                }
            }

            if ((tableSettings.Columns).findIndex(el => el.Type === 13) !== -1) {
                ports.push(new WebThingPort(
                    {
                        Name: 'onDropdownMenuItemClick',
                        PortKey: TableKeys.onDropdownMenuItemClick,
                        Type: Plegma.ePortType.JsonString,
                        ioDirection: Plegma.ioPortDirection.Output,
                        State: ""
                    },
                    WebThingPortType.Data)
                );
            }

            // different port for server data
            if (tableSettings.ServerSideProcessing) {
                ports.push(new WebThingPort({
                    Name: 'Server Data',
                    PortKey: TableKeys.ServerData,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Input,
                    State: ''
                }, WebThingPortType.Data));

                ports.push(new WebThingPort({
                    Name: 'Fetch Data',
                    PortKey: TableKeys.FetchData,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Output,
                    State: ''
                }, WebThingPortType.Data));
            }

            ports.push(new WebThingPort({
                Name: 'Selected data',
                PortKey: TableKeys.SelectDataPortKey,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: tableSettings.Selectable ? Plegma.ePortConf.None : Plegma.ePortConf.Hidden,
                State: ""
            }, WebThingPortType.Data));

            ports.push(new WebThingPort({
                Name: 'Edit data',
                PortKey: TableKeys.EditDataPortKey,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: tableSettings.Editable ? Plegma.ePortConf.None : Plegma.ePortConf.Hidden,
                State: ""
            }, WebThingPortType.Trigger));

            ports.push(new WebThingPort({
                Name: 'Add data',
                PortKey: TableKeys.AddDataPortKey,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: tableSettings.Editable ? Plegma.ePortConf.None : Plegma.ePortConf.Hidden,
                State: ""
            }, WebThingPortType.Trigger));

            ports.push(new WebThingPort({
                Name: 'onCellClick',
                PortKey: TableKeys.onCellClick,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ""
            }, WebThingPortType.Data));

            ports.push(new WebThingPort({
                Name: 'onPageChange',
                PortKey: TableKeys.onPageChange,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ""
            }, WebThingPortType.Data));

            ports.push(new WebThingPort({
                Name: 'onToolbarIconClick',
                PortKey: TableKeys.onToolbarIconClick,
                Type: Plegma.ePortType.String,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ""
            }, WebThingPortType.Data));

            ports.push(new WebThingPort({
                Name: 'onRowUpdate',
                PortKey: TableKeys.onRowUpdate,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            }, WebThingPortType.Data));

            ports.push(new WebThingPort({
                Name: 'onVisibilityChange',
                PortKey: TableKeys.onVisibilityChange,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ""
            }, WebThingPortType.Data));
        }
        return ports;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): TableData {
        return _.cloneDeep(DefaultTableSettings);
    }
    // ------------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: TableData): TableData {
        // If we have old settings pass them
        if (isDefined(data.TableSettings)) {
            _.defaults(data, data.TableSettings, DefaultTableSettings);
            delete data.TableSettings;
        } else {
            // If we missing table settings then set the default values
            _.defaults(data, DefaultTableSettings);
        }

        // fix empty `TableSettings.Key`
        if (isStringEmpty(data.Key)) {
            data.Key = DefaultTableSettings.Key;
        }

        // Set the default values in each column
        const newWidth: number = 100 / data.Columns.length;
        data.Columns.forEach(c => {
            _.defaults(c, { Width: newWidth }, DefaultColumnSettings);

            if (c.Type == ColumnType.color) {
                c.Sortable = false;
                c.Filtering = false;
            }
        })
        return data;
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() {
        return TableWidget.GetDefaultWidgetDataStatic();
    }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        var data = TableWidget.GetDefaultWidgetDataStatic();

        // fill sample columns
        data.Columns = [{
            Key: getRandomSmallKey(), JsonName: "col1", Title: "Column 1",
            Type: ColumnType.text, IsPrimaryKey: false, Sortable: false, Filtering: false, Hidden: false,
            NonEditable: false, AutoHiddable: false, Tooltip: "", Icon: IconType.add, Width: 50, PivotBy: false, IsMandatory: false,
            CheckedIcon: "fa-check-square-o",
            UncheckedIcon: "fa-square-o",
            BackgroundColorAccessor: null,
            FontColorAccessor: null,
            StyleAccessor: null,
            VisibleInAdd: true
        },
        {
            Key: getRandomSmallKey(), JsonName: "col2", Title: "Column 2",
            Type: ColumnType.text, IsPrimaryKey: false, Sortable: true, Filtering: false, Hidden: false, IsMandatory: false,
            NonEditable: false, AutoHiddable: false, Tooltip: "", Icon: IconType.add, Width: 50, PivotBy: false,
            CheckedIcon: "fa-check-square-o",
            UncheckedIcon: "fa-square-o",
            BackgroundColorAccessor: null,
            FontColorAccessor: null,
            StyleAccessor: null,
            VisibleInAdd: true
        }];

        // fill sample `PageListSettings`
        data.PageListSettings = getDefaultPageListSettings();

        return {
            Title: "Table",
            WidgetTitle: "",
            Key: "TableWidget",
            Type: "TableWidget",
            Data: data,
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: IconURIPath + "/svg/table.svg"
        };
    }
    // ------------------------------------------------------------------------------------------------
    GetLocalizationDictionary(): LocalizationMap {
        let dict = {};

        // add widget's title (if any)
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            dict[WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        _.forEach(this.WidgetData.Columns, (v: ColumnSettings) => {
            // Add column's name to dictionary.
            dict[LocalizationLabelKey.column + v.Key] = v.Title;

            // For buttons, add also the tooltip.
            if (v.Type == ColumnType.button && !isStringEmpty(v.Tooltip))
                dict[LocalizationLabelKey.tooltip + v.Key] = v.Tooltip;

            // for enums, expose enum values
            if (v.Type == ColumnType.enum) {
                const enumEntry = _.find(this.WidgetData.EnumSettings, (e) => e.key == v.Key);
                if (!isDefined(enumEntry)) {
                    DebugEx.TraceCritical("Should not be here");
                    return;
                }
                _.forEach(enumEntry.data, (el) => {
                    if (!isStringEmpty(el.label))
                        dict[LocalizationLabelKey.enumValue + enumEntry.key + '_' + el.key] = el.label;
                })
            }
        });

        //Expose tooltips for custom icons
        _.forEach(this.WidgetData.ToolbarIcons, (v: ToolbarIconInfo) => {
            dict[LocalizationLabelKey.tooltip + "toolbar-icon" + v.id] = v.tooltip;
        });

        // Expose tooltips for fixed toolbar buttons
        _.forEach(ReactTableStatics.GetLocalizationDictionary(), (value, key) => {
            dict[key] = value;
        })

        if (this.WidgetData.ShowEditRow)
            dict[LocalizationLabelKey.editRowButton] = editRowTooltip;

        return dict;
    };
    // ------------------------------------------------------------------------------------------------
}
// ----------------------------------------------------------------------------------------------------